import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

/**
 * EpisodesPanel — publish recorded sets + manage episode lifecycle.
 * Extracted from RadioHostStudio.
 */
export default function EpisodesPanel({ slug, recorder, tracklistRef }) {
  const [episodes, setEpisodes] = useState([]);
  const [open, setOpen] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/radio/host/episodes/mine");
      setEpisodes(data || []);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const publish = async () => {
    if (!recorder.blobUrl) { toast.error("Record a set first"); return; }
    if (!title.trim()) { toast.error("Give your episode a title"); return; }
    setPublishing(true);
    try {
      const resp = await fetch(recorder.blobUrl);
      const blob = await resp.blob();
      const fd = new FormData();
      fd.append("station_slug", slug);
      fd.append("title", title.trim());
      fd.append("description", description.trim());
      fd.append("duration_sec", String(recorder.durationSec || 0));
      fd.append("tracklist_json", JSON.stringify(tracklistRef?.current || []));
      fd.append("audio", blob, recorder.suggestedFilename || `tapin-${Date.now()}.webm`);
      const tok = localStorage.getItem("tv_token") || "";
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/radio/host/episodes`, {
        method: "POST", body: fd,
        headers: tok ? { Authorization: `Bearer ${tok}` } : {},
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success("Episode saved as draft");
      setTitle(""); setDescription("");
      load();
    } catch (e) { toast.error(`Upload failed: ${e.message || e}`); }
    finally { setPublishing(false); }
  };

  const setStatus = async (ep, status) => {
    try {
      await api.patch(`/radio/host/episodes/${ep.id}`, { status });
      toast.success(status === "published" ? "Published to listeners" : `Moved to ${status}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const share = async (ep) => {
    const url = `${window.location.origin}/radio/station/${ep.station_slug}#ep-${ep.id}`;
    try { await navigator.clipboard.writeText(url); toast.success("Share link copied"); } catch {}
  };
  const remove = async (ep) => {
    if (!window.confirm(`Delete "${ep.title}"? The audio file will be removed.`)) return;
    try { await api.delete(`/radio/host/episodes/${ep.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const statusChip = (s) => ({
    draft: "bg-white/10 text-white/70",
    published: "bg-emerald-500/25 text-emerald-200 border border-emerald-500/40",
    archived: "bg-white/5 text-white/50 border border-white/10",
  }[s] || "bg-white/10 text-white/70");

  return (
    <div data-testid="radio-host-episodes" className="rounded-lg border border-white/10 bg-black/30">
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center gap-2 px-3 py-2 text-left">
        <span className="text-[14px]">🎙️</span>
        <div className="flex-1 min-w-0">
          <div className="text-white font-black text-[11px] uppercase tracking-wider">Episodes</div>
          <div className="text-white/50 text-[10px]">
            {recorder.blobUrl && !recorder.recording ? "Publish your last recording · " : ""}
            {episodes.length} saved
          </div>
        </div>
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2">
          {/* Publish draft of the most recent recording */}
          {recorder.blobUrl && !recorder.recording && (
            <div data-testid="episode-publish-card" className="bg-fuchsia-500/10 border border-fuchsia-500/40 rounded p-2 space-y-1.5">
              <div className="text-[9px] uppercase tracking-wider font-black text-fuchsia-200">Publish your last set</div>
              <input
                data-testid="episode-title"
                placeholder="Episode title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full bg-black/50 border border-white/10 rounded px-2 py-1 text-white text-[11px]"
              />
              <textarea
                data-testid="episode-description"
                placeholder="Describe the set (optional)"
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full bg-black/50 border border-white/10 rounded px-2 py-1 text-white text-[10px] resize-none"
              />
              <button
                data-testid="episode-publish-submit"
                onClick={publish}
                disabled={publishing}
                className="w-full bg-fuchsia-500 hover:bg-fuchsia-400 disabled:opacity-40 text-black font-black text-[10px] py-1.5 rounded"
              >{publishing ? "Uploading…" : "Save as Draft"}</button>
              <div className="text-white/50 text-[9px]">After saving you can publish, share, archive or delete — below.</div>
            </div>
          )}

          {/* Episode list */}
          <div className="space-y-1" data-testid="episodes-list">
            {episodes.length === 0 && (
              <div className="text-white/40 text-[10px] text-center py-2">
                {recorder.blobUrl ? "Save your recording above to see it here." : "No episodes yet — hit REC in Broadcast bar above."}
              </div>
            )}
            {episodes.map((ep) => (
              <div key={ep.id} data-testid={`episode-row-${ep.id}`} className="bg-white/5 rounded p-2">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded bg-white/10 shrink-0" style={{ backgroundImage: `url(${ep.cover_url})`, backgroundSize: "cover" }} />
                  <div className="flex-1 min-w-0">
                    <div className="text-white text-[11px] font-bold truncate">{ep.title}</div>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <span className={`text-[8px] uppercase tracking-wider font-black px-1.5 py-0.5 rounded ${statusChip(ep.status)}`}>{ep.status}</span>
                      <span className="text-white/50 text-[9px]">{Math.floor((ep.duration_sec || 0) / 60)}:{String((ep.duration_sec || 0) % 60).padStart(2, "0")}</span>
                      {ep.plays_count > 0 && <span className="text-white/50 text-[9px]">· {ep.plays_count} plays</span>}
                    </div>
                  </div>
                </div>
                <div className="flex gap-1 mt-1.5 flex-wrap">
                  {ep.status === "draft" && (
                    <button data-testid={`episode-publish-${ep.id}`} onClick={() => setStatus(ep, "published")} className="bg-emerald-500 hover:bg-emerald-400 text-black font-black text-[9px] px-2 py-1 rounded">Publish</button>
                  )}
                  {ep.status === "published" && (
                    <button onClick={() => setStatus(ep, "archived")} className="bg-white/10 hover:bg-white/20 text-white font-black text-[9px] px-2 py-1 rounded">Archive</button>
                  )}
                  {ep.status === "archived" && (
                    <button onClick={() => setStatus(ep, "published")} className="bg-emerald-500 hover:bg-emerald-400 text-black font-black text-[9px] px-2 py-1 rounded">Re-publish</button>
                  )}
                  <button onClick={() => share(ep)} className="bg-white/10 hover:bg-white/20 text-white font-black text-[9px] px-2 py-1 rounded">Share</button>
                  <a href={ep.audio_url} target="_blank" rel="noreferrer" className="bg-white/10 hover:bg-white/20 text-white font-black text-[9px] px-2 py-1 rounded">Preview</a>
                  <button onClick={() => remove(ep)} className="bg-red-500/20 hover:bg-red-500/40 text-red-200 font-black text-[9px] px-2 py-1 rounded">Delete</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
